import type { TileFilterCapped } from "@fortex/api";
import type { ResultItem } from "../../core/store";
import type { Labels } from "../../i18n/labels";
import { format } from "../../i18n/labels";
import { zoomToOpenFeature } from "../featureZoom";
import { useCatalogue, useLabels, useLayers, useMapCore, useMapStore } from "../hooks";
import { Icon } from "../icons";
import type { IconName } from "../icons";
import { objectTitle } from "../fieldValues";
import { layerTitle } from "../layerTreeModel";
import { EmptyState } from "../primitives/EmptyState";

export type ResultsPaneProps = { className?: string };

export function resultKey(item: ResultItem, index: number): string {
  switch (item.kind) {
    case "object":
      return `object:${item.object.id}`;
    case "feature":
      return `feature:${item.hit.layer}:${item.hit.id}:${index}`;
    case "address":
      return `address:${item.hit.lat},${item.hit.lon}`;
  }
}

export function resultIcon(item: ResultItem): IconName {
  return item.kind === "object" ? "point" : item.kind === "feature" ? "tiles" : "home";
}

export function resultTitle(item: ResultItem): string {
  switch (item.kind) {
    case "object":
      return objectTitle(item.object);
    case "feature":
      return item.hit.name.length > 0 ? item.hit.name : item.hit.id;
    case "address":
      return item.hit.displayName;
  }
}

export function cappedNotes(capped: TileFilterCapped[], labels: Labels, layerName: (layer: string) => string | null = () => null): { layer: string; text: string }[] {
  const byLayer = new Map<string, number>();

  for (const entry of capped) byLayer.set(entry.layer, (byLayer.get(entry.layer) ?? 0) + entry.maxResults);

  return [...byLayer].map(([layer, count]) => ({ layer, text: format(labels.resultsCapped, { layer: layerName(layer) ?? layer, count: String(count) }) }));
}

export function resultMeta(item: ResultItem, labels: Labels, layerName: (layer: string) => string | null = () => null): string | null {
  switch (item.kind) {
    case "object":
      return item.object.typeName;
    case "feature":
      return layerName(item.hit.layer) ?? item.hit.layer;
    case "address":
      return labels.searchAddress;
  }
}

export function ResultsPane({ className }: ResultsPaneProps) {
  const core = useMapCore();
  const labels = useLabels();
  const results = useMapStore(a => a.results);
  const catalogue = useCatalogue();
  const { list } = useLayers();
  const layerName = (layer: string) => layerTitle(catalogue, list, layer);

  const pick = async (item: ResultItem) => {
    if (item.kind === "object") {
      core.search.pickObject(item.object);
      return;
    }

    if (item.kind === "address") {
      core.search.pickAddress({ lat: item.hit.lat, lon: item.hit.lon, boundingBox: item.hit.boundingBox });
      return;
    }

    if (item.latlng !== null && item.zoom !== null) {
      await core.info.openHit(item.hit, item.latlng, item.zoom);
      return;
    }

    await core.info.openFeature(item.hit.layer, item.hit.id, results.source === "search" ? results.term : null);
    zoomToOpenFeature(core);
  };

  if (results.source === null) return <EmptyState icon="search" title={labels.resultsTab} text={labels.resultsEmpty} />;

  return (
    <div className={["fxmap-results", className ?? ""].filter(Boolean).join(" ")} data-fxmap="results" data-fxmap-source={results.source}>
      <p className="fxmap-result-heading fxmap-muted">{results.source === "click" ? labels.featureChooser : format(labels.resultsFor, { term: results.term })}</p>
      {cappedNotes(results.capped ?? [], labels, layerName).map(note => (
        <p key={note.layer} className="fxmap-result-capped fxmap-muted" data-fxmap="result-capped" data-fxmap-layer={note.layer}>
          {note.text}
        </p>
      ))}
      <ul className="fxmap-result-list" role="list">
        {results.items.map((item, index) => (
          <li key={resultKey(item, index)} role="listitem">
            <button type="button" className="fxmap-result-item" data-fxmap="result-item" data-fxmap-kind={item.kind} onClick={() => void pick(item)}>
              <Icon name={resultIcon(item)} />
              <span className="fxmap-result-text">
                <span className="fxmap-result-name fxmap-truncate">{resultTitle(item)}</span>
                {resultMeta(item, labels, layerName) ? <span className="fxmap-result-meta fxmap-muted fxmap-truncate">{resultMeta(item, labels, layerName)}</span> : null}
              </span>
              <Icon name="chevronRight" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
